import { useEffect, useState } from 'react'
import Box from '@mui/material/Box'
import Modal from '@mui/material/Modal'
import { Column } from 'react-table'
import styles from './styles.module.css'
import Table from '../../../global/components/table/Table'
import NoFilter from '../../../global/components/table/helpers/filters/NoFilter'
import SecondaryButton from '../../../global/components/buttons/secondary-button/SecondaryButton'
import { fetchData } from '../../../global/utils/fetch'
import { formatDate } from '../../../global/utils/date/mm-dd-yyyy'
import { IAuditImage } from '../../../global/types/audit'
import {
  CLEANLINESS_RANK_BY,
  CLEANLINESS_WINDOW_DAYS,
} from '../../../global/constants/cleanliness'
import {
  CleanlinessRankBy,
  ICleanlinessInspectionDetail,
  ICleanlinessPhotoDetail,
  ICleanlinessRelatedSummary,
  ICleanlinessSubjectDetail,
} from '../../../../../app/types/cleanliness-score'
import { getCleanlinessSubjectDetail } from '../api'
import CleanlinessScoreChip from '../common/cleanliness-score/CleanlinessScoreChip'
import CleanlinessTrend from '../common/cleanliness-score/CleanlinessTrend'
import FullScreenImage from '../common/images-display/FullScreenImage'

interface ICleanlinessDetailModalProps {
  isOpen: boolean
  onClose: () => void
  rankBy: CleanlinessRankBy
  subjectObjectId: string | null
  subjectName: string
}

interface IPhotoCardProps {
  photo: ICleanlinessPhotoDetail
  onOpenImage: (image: IAuditImage) => void
}

function PhotoCard({ photo, onOpenImage }: IPhotoCardProps) {
  const url = photo.imageUrls[0]
  return (
    <div className={styles.photo}>
      {url ? (
        <img
          src={url}
          alt={photo.sectionTitle}
          onClick={() => onOpenImage({ url, type: photo.sectionTitle })}
        />
      ) : (
        <div className={styles.photoMissing}>No photo</div>
      )}
      <div className={styles.photoCaption}>
        <div className={styles.photoTitleRow}>
          <span className={styles.photoTitle}>{photo.sectionTitle}</span>
          <CleanlinessScoreChip
            score={photo.score}
            label={photo.unscorable ? 'Unscorable' : undefined}
          />
        </div>
        <p className={styles.photoReason}>{photo.reason}</p>
      </div>
    </div>
  )
}

interface IInspectionCardProps {
  inspection: ICleanlinessInspectionDetail
  rankBy: CleanlinessRankBy
  expanded: boolean
  onToggle: () => void
  onOpenImage: (image: IAuditImage) => void
}

function InspectionCard({
  inspection,
  rankBy,
  expanded,
  onToggle,
  onOpenImage,
}: IInspectionCardProps) {
  const isByVan = rankBy === CLEANLINESS_RANK_BY.van
  const relatedName = isByVan ? inspection.technicianName : inspection.vanName
  const relatedLabel = isByVan ? 'Driven by' : 'Van'

  return (
    <div
      className={`${styles.inspection} ${
        inspection.inCurrentWindow ? '' : styles.aged
      }`}
    >
      <button
        type='button'
        className={styles.inspectionSummary}
        onClick={onToggle}
        aria-expanded={expanded}
      >
        <span className={styles.inspectionDate}>
          {formatDate(new Date(inspection.operationDate))}
        </span>
        <span className={styles.inspectionMeta}>
          {relatedLabel} {relatedName ?? '-'}
        </span>
        {inspection.handover && (
          <span
            className={styles.badge}
            title='The previous DVIR on this van was by a different technician. This inspection counts toward the van, not the technician.'
          >
            New driver
          </span>
        )}
        <span className={styles.inspectionRight}>
          <span className={styles.inspectionMeta}>
            {`${inspection.photosScored} of ${inspection.photosTotal} photos scored`}
          </span>
          <CleanlinessScoreChip score={inspection.score} />
          <span className={styles.chevron}>{expanded ? '▲' : '▼'}</span>
        </span>
      </button>
      {expanded && (
        <div className={styles.photos}>
          {inspection.photos.map((photo) => (
            <PhotoCard
              key={photo.sectionKey}
              photo={photo}
              onOpenImage={onOpenImage}
            />
          ))}
        </div>
      )}
    </div>
  )
}

function CleanlinessDetailModal({
  isOpen,
  onClose,
  rankBy,
  subjectObjectId,
  subjectName,
}: ICleanlinessDetailModalProps) {
  const [detail, setDetail] = useState<ICleanlinessSubjectDetail | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [loadFailed, setLoadFailed] = useState(false)
  const [expandedIds, setExpandedIds] = useState<string[]>([])
  const [fullScreenImage, setFullScreenImage] = useState<IAuditImage | null>(
    null,
  )

  const isByVan = rankBy === CLEANLINESS_RANK_BY.van

  useEffect(() => {
    if (!isOpen || !subjectObjectId) return

    const abortController = new AbortController()
    setDetail(null)
    setExpandedIds([])
    setIsLoading(true)
    setLoadFailed(false)

    fetchData<ICleanlinessSubjectDetail>(
      getCleanlinessSubjectDetail(
        rankBy,
        subjectObjectId,
        CLEANLINESS_WINDOW_DAYS,
      ),
      abortController.signal,
    )
      .then((response) => {
        setDetail(response)
        // Open the newest inspection so the photos are one click closer.
        const newest = response.inspections.find((i) => i.inCurrentWindow)
        if (newest) setExpandedIds([newest.cleanlinessScoreObjectId])
      })
      .catch((error) => {
        if (abortController.signal.aborted) return
        console.error('Failed to load cleanliness detail:', error)
        setLoadFailed(true)
      })
      .finally(() => {
        if (!abortController.signal.aborted) setIsLoading(false)
      })

    return () => abortController.abort()
  }, [isOpen, rankBy, subjectObjectId])

  function toggleExpanded(id: string) {
    setExpandedIds((current) =>
      current.includes(id)
        ? current.filter((value) => value !== id)
        : [...current, id],
    )
  }

  const relatedColumns: Column<ICleanlinessRelatedSummary>[] = [
    {
      Header: isByVan ? 'Technician' : 'Van',
      accessor: 'name',
      Filter: NoFilter(),
    },
    {
      Header: `Score, last ${CLEANLINESS_WINDOW_DAYS} days`,
      accessor: 'rollingScore',
      Filter: NoFilter(),
      Cell: ({
        row: { original },
      }: {
        row: { original: ICleanlinessRelatedSummary }
      }) => <CleanlinessScoreChip score={original.rollingScore} />,
    },
    {
      Header: 'DVIRs scored',
      accessor: 'inspectionsScored',
      Filter: NoFilter(),
      Cell: ({
        row: { original },
      }: {
        row: { original: ICleanlinessRelatedSummary }
      }) => (
        <>{`${original.inspectionsScored} of ${original.inspectionsInWindow}`}</>
      ),
    },
  ]

  const currentInspections =
    detail?.inspections.filter((i) => i.inCurrentWindow) ?? []
  const agedInspections =
    detail?.inspections.filter((i) => !i.inCurrentWindow) ?? []

  return (
    <Modal
      open={isOpen}
      onClose={onClose}
    >
      <Box className={styles.modal}>
        <div className={styles.header}>
          <div>
            <div className={styles.eyebrow}>
              {isByVan ? 'Van' : 'Technician'}
              {detail?.locationName ? ` · ${detail.locationName}` : ''}
            </div>
            <h2>{detail?.subjectName ?? subjectName}</h2>
          </div>
          {detail && (
            <div className={styles.headline}>
              <CleanlinessScoreChip
                score={detail.rollingScore}
                size='large'
              />
              <div className={styles.headlineLabel}>
                <strong>{`Score, last ${detail.windowDays} days`}</strong>
                <span>
                  {'Change vs. the prior '}
                  {detail.windowDays}
                  {' days: '}
                  <CleanlinessTrend
                    current={detail.rollingScore}
                    previous={detail.previousRollingScore}
                  />
                </span>
              </div>
            </div>
          )}
        </div>

        {isLoading && <div className={styles.state}>Loading...</div>}
        {loadFailed && (
          <div className={styles.state}>
            Cleanliness details are not available right now.
          </div>
        )}

        {detail && (
          <>
            {detail.related.length > 1 && (
              <div className={styles.section}>
                <h3>{isByVan ? 'By technician' : 'By van'}</h3>
                <p className={styles.sectionNote}>
                  {isByVan
                    ? 'More than one technician drove this van in the window. Each line is that technician’s inspections of this van only.'
                    : 'This technician drove more than one van in the window. Each line is their inspections in that van only.'}
                </p>
                <Table
                  columns={relatedColumns}
                  data={detail.related}
                  isLoading={false}
                  isTableTopperPresent={false}
                  disableAllRowClicks
                  disableSortBy
                />
              </div>
            )}

            <div className={styles.section}>
              <h3>{`DVIRs, last ${detail.windowDays} days`}</h3>
              <p className={styles.sectionNote}>
                The score above is the average of these inspections. Open one to
                see its photos and how each was scored.
              </p>
              {currentInspections.length === 0 ? (
                <div className={styles.state}>
                  No scored DVIRs in this window.
                </div>
              ) : (
                <div className={styles.inspections}>
                  {currentInspections.map((inspection) => (
                    <InspectionCard
                      key={inspection.cleanlinessScoreObjectId}
                      inspection={inspection}
                      rankBy={rankBy}
                      expanded={expandedIds.includes(
                        inspection.cleanlinessScoreObjectId,
                      )}
                      onToggle={() =>
                        toggleExpanded(inspection.cleanlinessScoreObjectId)
                      }
                      onOpenImage={setFullScreenImage}
                    />
                  ))}
                </div>
              )}
            </div>

            {agedInspections.length > 0 && (
              <div className={styles.section}>
                <h3>{`Before the last ${detail.windowDays} days`}</h3>
                <p className={styles.sectionNote}>
                  No longer counted in the score. Listed so a change since last
                  week can be traced to the inspection that dropped out.
                </p>
                <div className={styles.inspections}>
                  {agedInspections.map((inspection) => (
                    <InspectionCard
                      key={inspection.cleanlinessScoreObjectId}
                      inspection={inspection}
                      rankBy={rankBy}
                      expanded={expandedIds.includes(
                        inspection.cleanlinessScoreObjectId,
                      )}
                      onToggle={() =>
                        toggleExpanded(inspection.cleanlinessScoreObjectId)
                      }
                      onOpenImage={setFullScreenImage}
                    />
                  ))}
                </div>
              </div>
            )}
          </>
        )}

        <div className={styles.actions}>
          <SecondaryButton
            buttonName='Close'
            onClick={onClose}
          />
        </div>

        <FullScreenImage
          image={fullScreenImage ?? { url: '', type: '' }}
          fullscreenVisible={!!fullScreenImage}
          setFullScreenVisible={(visible: boolean) => {
            if (!visible) setFullScreenImage(null)
          }}
        />
      </Box>
    </Modal>
  )
}

export default CleanlinessDetailModal
